/*
==================================================
APP TAP SOUND LISTENER

Функция:
глобальный слушатель кликов / тапов по интерфейсу.

Родитель:
подключается один раз в layout приложения.

Дочерние элементы:
нет

Использует:
src/lib/custom-ui/sounds/app-tap-sound.ts

Примечания:
- компонент ничего не рисует на странице
- слушает document click
- проигрывает звук только для интерактивных элементов
- не срабатывает для disabled-элементов
- не срабатывает при обычном вводе текста
- тип звука можно задать через data-tap-sound
- если data-tap-sound не задан, используется default
- нужен, чтобы не добавлять звук вручную в каждую кнопку
==================================================
*/

"use client"

import { useEffect } from "react"
import {
  AppTapSoundType,
  playAppTapSound,
} from "@lib/custom-ui/sounds/app-tap-sound"

const INTERACTIVE_SELECTOR = [
  "button",
  "a",
  "[role='button']",
  "select",
  "input[type='checkbox']",
  "input[type='radio']",
].join(",")

const TAP_SOUND_ATTRIBUTE = "data-tap-sound"

const SUPPORTED_SOUND_TYPES: AppTapSoundType[] = [
  "default",
  "category",
  "addToCart",
  "remove",
  "success",
  "close",
]

const resolveTapSoundType = (element: HTMLElement): AppTapSoundType => {
  const rawType = element.getAttribute(TAP_SOUND_ATTRIBUTE)

  if (SUPPORTED_SOUND_TYPES.includes(rawType as AppTapSoundType)) {
    return rawType as AppTapSoundType
  }

  return "default"
}

export default function AppTapSoundListener() {
  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null
      if (!target) return

      const interactiveElement = target.closest(INTERACTIVE_SELECTOR) as
        | HTMLElement
        | null

      if (!interactiveElement) return

      if (
        interactiveElement.hasAttribute("disabled") ||
        interactiveElement.getAttribute("aria-disabled") === "true"
      ) {
        return
      }

      const soundType = resolveTapSoundType(interactiveElement)

      playAppTapSound(soundType)
    }

    document.addEventListener("click", handleClick, true)

    return () => {
      document.removeEventListener("click", handleClick, true)
    }
  }, [])

  return null
}
